import classnames from "classnames";
import some from "lodash/some";
import pluralize from "pluralize";
import {ForwardedRef, forwardRef} from "react";
import {FiAlertTriangle} from "react-icons/fi";
import {SelectInstance} from "react-select";

import {Icon} from "@/components/icon/Icon.tsx";
import {TooltipWrapper} from "@/components/overlay/TooltipWrapper.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";
import {thousandSeparatedInt} from "@/lib/format.tsx";
import {SubscriptionPackTier, SubscriptionPackTierItem} from "@/pages/billing/v2/types.ts";

const _buildOptionLabel = ({
  limits,
  isCurrentPurchasedOption,
  isMenu,
}: {
  limits: SubscriptionPackTierItem[];
  isMenu: boolean;
  isCurrentPurchasedOption?: boolean | null;
}) => {
  return (
    <TooltipWrapper toolTip={isCurrentPurchasedOption && isMenu ? "This is your current option." : undefined}>
      <div className={classnames({"pulse-color-gray-500": isCurrentPurchasedOption && isMenu})}>
        {limits.map((limit, idx) => {
          const used = limit.limit_used;
          const value = limit.value;
          const overLimit = limit.value < used;
          let toolTip: string;
          if (limit.limit_field === "limit_monitoring_group_level") {
            toolTip = `Some of your checks are using Probe Locations above ${limit.value_alt} level. To Subscribe to this tier, please delete these checks.`;
          } else if (limit.limit_field === "limit_monitoring_groups") {
            toolTip = `Some of your checks are using more than ${value} Locations per Check. To Subscribe to this tier, please delete these checks.`;
          } else if (limit.limit_field === "limit_rum_checks_requests_per_month") {
            toolTip = `You currently have ${thousandSeparatedInt(used)} monthly RUM page views. To subscribe to this tier now, please remove RUM checks with active page views to bring your total under the maximum for this plan.`;
          } else {
            toolTip = `You currently have ${thousandSeparatedInt(used)} ${pluralize(limit.limit_name, used)} configured. To Subscribe to this tier, please delete ${used - value} ${pluralize(limit.limit_name, used - value)}.`;
          }
          return (
            <span key={idx} className="pulseTextBodySmall">
              <TooltipWrapper toolTip={overLimit ? toolTip : undefined}>
                <span
                  className={classnames({"": isCurrentPurchasedOption, "pulse-color-red-500": overLimit}, "d-flex")}
                >
                  {overLimit && <Icon removeDivDimensions icon={FiAlertTriangle} className="me-1" />}
                  {limit.value_alt || thousandSeparatedInt(value)} {limit.limit_name}
                  {idx < limits.length - 1 && <>,&nbsp;</>}
                </span>
              </TooltipWrapper>
            </span>
          );
        })}
      </div>
    </TooltipWrapper>
  );
};

export const PackSelect = forwardRef(
  (props: SelectProps<SubscriptionPackTier>, ref: ForwardedRef<SelectInstance<SubscriptionPackTier>>) => {
    return (
      <Select
        getOptionValue={(option) => option.tier_id}
        formatOptionLabel={(data, meta) =>
          _buildOptionLabel({
            limits: data.limits,
            isCurrentPurchasedOption: data.is_current,
            isMenu: meta.context === "menu",
          })
        }
        isOptionDisabled={(option) => some(option.limits.map((limit) => limit.value < limit.limit_used))}
        isClearable={false}
        isSearchable={false}
        ref={ref}
        {...props}
      />
    );
  }
);
